import type { FC } from 'react';
import { PrivacyStyles as styles } from '../css/styles';
import { Helmet } from 'react-helmet-async';

const PrivacyUS: FC = () => {
    return (
        <>
            <Helmet>
                <title>Privacy Policy — First Klass</title>
                <meta name="description" content="Privacy Policy for First Klass platform (www.first-klass.ru) — United States" />
            </Helmet>
            <div className={styles.privacyContainer}>
                <h1>Privacy Policy</h1>
                <p>
                    This Privacy Policy describes how <strong>First Klass</strong> (
                    <strong>www.first-klass.ru</strong>, hereinafter «we», «us», or «our»)
                    collects, uses, discloses, and safeguards personal information about users
                    in the United States. Where applicable, this Policy addresses requirements
                    under the California Consumer Privacy Act (CCPA/CPRA) and other applicable
                    US state privacy laws.
                </p>
                <p>
                    This Policy applies both to the First Klass website (www.first-klass.ru) and to
                    the First Klass mobile applications for iOS and Android (together, «the Platform»).
                    The Platform is used by students who take lessons with a tutor or school on First
                    Klass; student accounts are created by the tutor or school, not by public sign-up.
                </p>
                <p>
                    By accessing or using the Platform, you agree to the terms of this Privacy
                    Policy. If you disagree with any part of this Policy, please do not use
                    the Platform.
                </p>

                <h2>1. Information We Collect</h2>
                <p>We collect the following categories of personal information:</p>
                <ul>
                    <li>
                        <strong>Identifiers</strong> — name, email address, phone number,
                        account username, and IP address;
                    </li>
                    <li>
                        <strong>Account and profile information</strong> — profile details,
                        preferences, and settings you provide;
                    </li>
                    <li>
                        <strong>Internet or network activity</strong> — browsing history on the
                        Platform, pages viewed, features used, and session information;
                    </li>
                    <li>
                        <strong>Device and technical data</strong> — browser type, operating system,
                        device identifiers, and cookie data;
                    </li>
                    <li>
                        <strong>Audio recordings</strong> — voice recordings you make when completing
                        speaking tasks in the mobile app, stored so your tutor can review them;
                    </li>
                    <li>
                        <strong>User content</strong> — written answers, homework, and test responses
                        you submit through the app;
                    </li>
                    <li>
                        <strong>Education records</strong> — course progress, test scores, homework
                        grades, lesson attendance, lesson logs, and rewards earned;
                    </li>
                    <li>
                        <strong>Communications</strong> — messages sent to us via support channels
                        or contact forms.
                    </li>
                </ul>
                <p>
                    We do not collect sensitive personal information such as Social Security numbers,
                    financial account details, precise geolocation, or health data. Voice recordings
                    are used only so your tutor can review your spoken answers; they are not used
                    for identification and are not treated as biometric information.
                </p>
                <p>
                    Your audio recordings, user content, and education records are visible within
                    the Platform only to your own tutor and the school or organization through which
                    you were enrolled.
                </p>

                <h2>2. How We Collect Information</h2>
                <ul>
                    <li>
                        <strong>Directly from you</strong> — when you register, fill in forms,
                        or contact us;
                    </li>
                    <li>
                        <strong>Automatically</strong> — via cookies, web beacons, and similar
                        technologies when you use the Platform;
                    </li>
                    <li>
                        <strong>From third parties</strong> — such as authentication providers
                        or analytics services, subject to their own privacy policies.
                    </li>
                </ul>

                <h2>3. How We Use Your Information</h2>
                <ul>
                    <li>To create and manage your account and deliver Platform services;</li>
                    <li>To process payments and fulfil subscription orders;</li>
                    <li>To send service-related notifications and respond to your requests;</li>
                    <li>To send promotional communications where you have opted in;</li>
                    <li>To analyse usage patterns and improve Platform performance;</li>
                    <li>To detect, prevent, and address fraud, security, or technical issues;</li>
                    <li>To comply with applicable laws and legal obligations.</li>
                </ul>

                <h2>4. Disclosure of Your Information</h2>
                <p>We may share your personal information with:</p>
                <ul>
                    <li>
                        <strong>Service providers</strong> — third parties that perform services
                        on our behalf (hosting, analytics, payment processing, email delivery)
                        under confidentiality obligations;
                    </li>
                    <li>
                        <strong>Business transfers</strong> — in connection with a merger, acquisition,
                        or sale of assets, with notice to affected users;
                    </li>
                    <li>
                        <strong>Legal compliance</strong> — when required by law, court order,
                        or governmental authority, or to protect our legal rights.
                    </li>
                </ul>
                <p>We do not sell your personal information to third parties.</p>

                <h2>5. Cookies and Tracking Technologies</h2>
                <p>
                    We use cookies and similar tracking technologies to operate the Platform and
                    improve your experience. You can manage cookie preferences through your browser
                    settings. Disabling certain cookies may affect Platform functionality.
                </p>
                <p>
                    We currently do not respond to «Do Not Track» (DNT) browser signals, as there
                    is no industry-wide standard for DNT compliance. We will update this Policy
                    if that changes.
                </p>

                <h2>6. Mobile Applications and Device Permissions</h2>
                <p>
                    The First Klass mobile applications request the following device permission:
                </p>
                <ul>
                    <li>
                        <strong>Microphone</strong> — requested only when you start a speaking task,
                        so the app can record your spoken answer. Recordings are uploaded to First
                        Klass and made available to your tutor for review and grading. They are not
                        used for any other purpose, not used to identify you by voice, and not sold
                        or shared with any third party. You may decline or later revoke this
                        permission in your device settings; speaking tasks will then be unavailable,
                        but the rest of the app continues to work.
                    </li>
                </ul>
                <p>
                    The mobile applications do <strong>not</strong> use third-party analytics,
                    advertising, or tracking technologies, and do not collect any advertising
                    identifier (such as the IDFA). The cookie and tracking practices in Section 5
                    apply to the website only.
                </p>
                <p>
                    You can permanently delete your account, together with your audio recordings and
                    user content, from within the app (Profile → Delete Account). Deletion is
                    confirmed by an email link.
                </p>

                <h2>7. Data Retention</h2>
                <p>
                    We retain your personal information for as long as your account is active or
                    as needed to provide services. Upon account deletion, we delete or de-identify
                    your personal data within a reasonable timeframe, except where retention is
                    required by law.
                </p>

                <h2>8. Your Privacy Rights</h2>
                <p>
                    Depending on your state of residence, you may have the following rights:
                </p>
                <ul>
                    <li>
                        <strong>Right to know</strong> — to request disclosure of the categories
                        and specific pieces of personal information we have collected about you;
                    </li>
                    <li>
                        <strong>Right to delete</strong> — to request deletion of personal information
                        we have collected, subject to certain exceptions;
                    </li>
                    <li>
                        <strong>Right to correct</strong> — to request correction of inaccurate
                        personal information;
                    </li>
                    <li>
                        <strong>Right to opt out</strong> — to opt out of the sale or sharing of
                        personal information (we do not sell personal data);
                    </li>
                    <li>
                        <strong>Right to non-discrimination</strong> — we will not discriminate
                        against you for exercising any of your privacy rights.
                    </li>
                </ul>
                <p>
                    To submit a privacy rights request, contact us through the Platform's contact
                    form. We will verify your identity and respond within 45 days, as required
                    by applicable law.
                </p>

                <h2>9. Children's Privacy</h2>
                <p>
                    The Platform is not directed to the general public and is not available through
                    public sign-up. Student accounts, including any accounts for minors, are created
                    by a tutor or school, who is responsible for obtaining any parental or guardian
                    consent required by law before enrolling a student. We do not knowingly collect
                    personal information directly from children without such consent. If you believe
                    a child's information has been provided to us improperly, please contact us and
                    we will promptly delete it.
                </p>

                <h2>10. Security</h2>
                <p>
                    We implement reasonable administrative, technical, and physical safeguards
                    to protect your personal information. However, no method of transmission
                    over the internet is 100% secure. We encourage you to use a strong, unique
                    password and to contact us immediately if you suspect unauthorised access
                    to your account.
                </p>

                <h2>11. Changes to This Policy</h2>
                <p>
                    We may update this Privacy Policy periodically. We will notify you of material
                    changes by posting the updated Policy on the Platform and, where required,
                    by email. The current version is always available at{' '}
                    <strong>www.first-klass.ru</strong>.
                </p>
                <p>
                    <strong>Version date: August 31, 2026</strong>
                </p>
            </div>
        </>
    );
};

export default PrivacyUS;
